import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ErrorText from '../../atoms/ErrorText/ErrorText.js';
import SuccessMessage from '../../molecules/SuccessMessage/index.js';

import { StyledText } from '../../atoms/Text/StyledText.js';
import { StyledHourWrapper } from '../../atoms/TrackHours/StyledHourWrapper.js';
import { StyledForm } from '../../atoms/TrackHours/StyledForm.js';
import { StyledSelectWrapper } from '../../atoms/TrackHours/StyledSelectWrapper.js';
import { StyledInputsButtonsWrapper } from '../../atoms/TrackHours/StyledInputsButtonsWrapper.js';
import { StyledInputwrapper } from '../../atoms/TrackHours/StyledInputwrapper.js';

import { getString } from '../../../strings/index.js';
import { hasPermission } from '../../../logic/permissions.js';
import { getDateString } from '../../../logic/isotime.js';

const ManagerHoursForm = ({ user, types, managers, onSubmit }) => {
    const [errors, setErrors] = useState({});
    const [submitSucc, setSubmitSucc] = useState(false);
    const [submitErr, setSubmitErr] = useState(false);
    const [submitErrMsg, setSubmitErrMsg] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const canManageAll = useMemo(() => hasPermission(user, 'can_manage_all_manager_hours'), [user]);

    const form = useRef();
    const type = useRef();
    const manager = useRef();
    const description = useRef();
    const hours = useRef();
    const date = useRef();
    const refs = useMemo(() => ({
        type,
        manager,
        description,
        hours,
        date,
    }), [type, manager, description, hours, date]);

    const getRefsData = () => ({
        type: parseInt(refs.type.current.value),
        ...(canManageAll ? { manager: parseInt(refs.manager.current.value) } : {}),
        description: refs.description.current.value,
        hours: parseFloat(refs.hours.current.value),
        date: refs.date.current.value,
    });

    useEffect(() => {
        if (submitSucc) {
            setErrors({});
            const timeout = setTimeout(() => setSubmitSucc(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [submitSucc]);

    useEffect(() => {
        if (submitErr) {
            const timeout = setTimeout(() => setSubmitErr(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [submitErr]);

    const validateFields = useCallback(() => {
        const newErrors = {};

        if (refs.type.current && !parseInt(refs.type.current.value)) {
            newErrors.type = true;
        }
        if (canManageAll && refs.manager.current && !parseInt(refs.manager.current.value)) {
            newErrors.manager = true;
        }
        if (refs.description.current && !refs.description.current.value.trim()) {
            newErrors.description = true;
        }
        if (
            refs.hours.current &&
            (!parseFloat(refs.hours.current.value) ||
                parseFloat(refs.hours.current.value) <= 0 ||
                parseFloat(refs.hours.current.value) % 0.25 !== 0)
        ) {
            newErrors.hours = true;
        }
        if (refs.date.current && !refs.date.current.value) {
            newErrors.date = true;
        }

        setErrors(newErrors);
        return Object.values(newErrors).every((v) => !v);
    }, [refs, canManageAll]);

    useEffect(() => {
        if (!isSubmitted) return;
        validateFields();
    }, [isSubmitted, validateFields]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setIsSubmitted(true);

        const formData = getRefsData();

        if (
            !formData.type ||
            isNaN(formData.type) ||
            !formData.description?.trim() ||
            !formData.hours ||
            isNaN(formData.hours) ||
            formData.hours <= 0 ||
            formData.hours % 0.25 !== 0 ||
            !formData.date ||
            (canManageAll && (!formData.manager || isNaN(formData.manager)))
        ) {
            setErrors({
                type: !formData.type || isNaN(formData.type),
                ...(canManageAll ? { manager: !formData.manager || isNaN(formData.manager) } : {}),
                description: !formData.description?.trim(),
                hours:
                    !formData.hours ||
                    isNaN(formData.hours) ||
                    formData.hours <= 0 ||
                    formData.hours % 0.25 !== 0,
                date: !formData.date,
            });
            return;
        }

        onSubmit?.(
            formData,
            () => {
                const typeName = types?.find((t) => t.id === formData.type)?.name || '';
                const managerObj = canManageAll
                    ? managers?.find((m) => m.id === formData.manager)
                    : null;
                setSubmitSucc({
                    type: typeName,
                    description: formData.description,
                    hours: formData.hours,
                    date: formData.date,
                    ...(managerObj
                        ? { manager: `${managerObj.lastname} ${managerObj.firstname}` }
                        : {}),
                });
                setErrors({});
                setIsSubmitted(false);
                form.current.reset();
                refs.type.current.value = 0;
                if (canManageAll) refs.manager.current.value = 0;
                refs.description.current.value = 'Standard';
                refs.hours.current.value = 1;
                refs.date.current.value = getDateString(new Date());
            },
            (err) => {
                setSubmitErr(true);
                setSubmitErrMsg(err.data.message);
            }
        );
    };

    return (
        <StyledHourWrapper>
            {submitSucc ? (
                <SuccessMessage
                    title={getString('manager_hours_form_submit_success') + ':'}
                    infos={[
                        {
                            title: getString('manager_hours_form_type') + ':',
                            value: submitSucc.type,
                        },
                        {
                            title: getString('manager_hours_form_description') + ':',
                            value: submitSucc.description,
                        },
                        {
                            title: getString('manager_hours_form_hours') + ':',
                            value: String(submitSucc.hours),
                        },
                        {
                            title: getString('manager_hours_form_date') + ':',
                            value: submitSucc.date.split('-').reverse().join('.'),
                        },
                        ...(submitSucc.manager
                            ? [
                                  {
                                      title: getString('manager_hours_form_manager') + ':',
                                      value: submitSucc.manager,
                                  },
                              ]
                            : []),
                    ]}
                />
            ) : undefined}
            {submitErr ? (
                <StyledText
                    hasdeclaredfontcolor="red"
                    hasdeclaredpadding="20px 0 40px"
                    hasdeclaredfontsize="22px"
                    hasdeclaredfontweight="700"
                    hasdeclaredtextalign="center"
                    hasdeclaredlineheight="1.4em"
                    as="p"
                >
                    {getString('UserPanelTrainerTrackHours__form_submit__fail')} ({submitErrMsg})
                </StyledText>
            ) : undefined}
            <StyledForm ref={form} onSubmit={handleFormSubmit}>
                <StyledSelectWrapper>
                    <StyledInputwrapper error={errors.type}>
                        <label htmlFor="managerHourType">
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredtextalign="center"
                                hasdeclaredpadding="8px 0 8px 0"
                                hasdeclaredfontweight="600"
                            >
                                {getString('manager_hours_form_type')}:
                            </StyledText>
                        </label>
                        <select ref={refs.type} id="managerHourType" defaultValue={0}>
                            <option value={0}>
                                - {getString('manager_hours_form_select_type')} -
                            </option>
                            {types?.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                        {errors.type ? (
                            <ErrorText text={getString('user_panel_to_pole_jest_wymagane')} />
                        ) : null}
                    </StyledInputwrapper>
                    <StyledInputwrapper error={errors.hours}>
                        <label htmlFor="managerHourHours">
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredtextalign="center"
                                hasdeclaredpadding="8px 0 8px 0"
                                hasdeclaredfontweight="600"
                            >
                                {getString('manager_hours_form_hours')}:
                            </StyledText>
                        </label>
                        <input
                            ref={refs.hours}
                            id="managerHourHours"
                            type="number"
                            min={0.25}
                            max={99}
                            step={0.25}
                            defaultValue={1}
                        />
                        {errors.hours ? (
                            <ErrorText text={getString('user_panel_to_pole_jest_wymagane')} />
                        ) : null}
                    </StyledInputwrapper>
                    <StyledInputwrapper error={errors.date}>
                        <label htmlFor="managerHourDate">
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredtextalign="center"
                                hasdeclaredpadding="8px 0 8px 0"
                                hasdeclaredfontweight="600"
                            >
                                {getString('manager_hours_form_date')}:
                            </StyledText>
                        </label>
                        <input
                            ref={refs.date}
                            id="managerHourDate"
                            type="date"
                            defaultValue={getDateString(new Date())}
                        />
                        {errors.date ? (
                            <ErrorText text={getString('user_panel_to_pole_jest_wymagane')} />
                        ) : null}
                    </StyledInputwrapper>
                    {canManageAll ? (
                        <StyledInputwrapper error={errors.manager}>
                            <label htmlFor="managerHourManager">
                                <StyledText
                                    hasdeclaredfontsize="20px"
                                    hasdeclaredtextalign="center"
                                    hasdeclaredpadding="8px 0 8px 0"
                                    hasdeclaredfontweight="600"
                                >
                                    {getString('manager_hours_form_manager')}:
                                </StyledText>
                            </label>
                            <select ref={refs.manager} id="managerHourManager" defaultValue={0}>
                                <option value={0}>
                                    - {getString('manager_hours_form_select_manager')} -
                                </option>
                                {managers?.map((manager) => (
                                    <option key={manager.id} value={manager.id}>
                                        {`${manager.lastname} ${manager.firstname}`}
                                    </option>
                                ))}
                            </select>
                            {errors.manager ? (
                                <ErrorText text={getString('user_panel_to_pole_jest_wymagane')} />
                            ) : null}
                        </StyledInputwrapper>
                    ) : null}
                </StyledSelectWrapper>
                <StyledSelectWrapper>
                    <StyledInputwrapper error={errors.description} hasdeclaredwidth="100%">
                        <label htmlFor="managerHourDescription">
                            <StyledText
                                hasdeclaredfontsize="20px"
                                hasdeclaredtextalign="center"
                                hasdeclaredpadding="8px 0 8px 0"
                                hasdeclaredfontweight="600"
                            >
                                {getString('manager_hours_form_description')}:
                            </StyledText>
                        </label>
                        <input
                            ref={refs.description}
                            id="managerHourDescription"
                            type="text"
                            defaultValue="Standard"
                        />
                        {errors.description ? (
                            <ErrorText text={getString('user_panel_to_pole_jest_wymagane')} />
                        ) : null}
                    </StyledInputwrapper>
                </StyledSelectWrapper>
                <StyledInputsButtonsWrapper>
                    <input
                        type="submit"
                        value={getString('UserPanelTrainerTrackHours__form__add')}
                    />
                    <input
                        type="reset"
                        value={getString('UserPanelTrainerTrackHours__form__cancel')}
                    />
                </StyledInputsButtonsWrapper>
            </StyledForm>
        </StyledHourWrapper>
    );
};

export default ManagerHoursForm;
